import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Loader2 } from "lucide-react";
import { PLANS, money, SITE } from "../data/site";
import { STATES, sortedAbbrs, NO_WAGE_TAX, LOCAL_TAX, PAID_LEAVE } from "../data/states";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500";
const labelCls = "block text-xs font-bold uppercase tracking-[0.12em] text-slate-500 mb-1.5";

const STEPS = ["Your team", "Pick a plan", "Your details"];

export const QuoteWizard = () => {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", business: "", email: "", phone: "",
    state: STATES[params.get("state")] ? params.get("state") : "TX",
    headcount: Math.min(500, Math.max(1, Number(params.get("headcount")) || 5)),
    current_provider: "None, first payroll",
    plan: PLANS[params.get("plan")] ? params.get("plan") : "essentials",
    message: "", website: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const n = Math.max(1, Number(form.headcount) || 1);
  const p = PLANS[form.plan];
  const ours = p.base + p.per * n;
  const theirs = p.cbase + p.cper * n;

  const next = () => setStep((s) => Math.min(s + 1, 2));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    if (step < 2 || !form.name || !form.business || !form.email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, headcount: n });
      setDone(true);
      toast.success("Quote request sent. We'll reply the same business day.");
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mt-12 rounded-2xl border border-emerald-200 bg-emerald-50 p-10 text-center" data-testid="contact-success">
        <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
        <h2 className="mt-4 font-display text-2xl font-bold text-slate-900">Request received</h2>
        <p className="mt-2 text-base text-slate-600">
          Thanks, {form.name.split(" ")[0]}. We emailed your instant estimate of{" "}
          <strong className="font-mono-num text-emerald-700">{money(ours)}/mo</strong> to {form.email} — a written
          quote for {form.business} follows the same business day.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-10" data-testid="quote-wizard">
      {/* Progress */}
      <ol className="flex items-center gap-2" data-testid="wizard-progress">
        {STEPS.map((label, i) => (
          <li key={label} className="flex-1">
            <button
              type="button"
              onClick={() => i < step && setStep(i)}
              data-testid={`wizard-step-indicator-${i}`}
              className={`w-full text-left ${i < step ? "cursor-pointer" : "cursor-default"}`}
            >
              <span className={`block h-1.5 rounded-full transition-colors duration-300 ${i <= step ? "bg-emerald-600" : "bg-slate-200"}`} />
              <span className={`mt-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${i <= step ? "text-emerald-700" : "text-slate-400"}`}>
                {i < step ? <Check size={12} /> : <span className="font-mono-num">{i + 1}.</span>} {label}
              </span>
            </button>
          </li>
        ))}
      </ol>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
        {/* Step content */}
        <form onSubmit={submit} className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-7" data-testid="contact-form">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              {step === 0 && (
                <div className="space-y-5">
                  <h2 className="font-display text-xl font-bold text-slate-900">Tell us about your team</h2>
                  <div>
                    <label className={labelCls} htmlFor="w-state">Main state</label>
                    <select id="w-state" data-testid="contact-state-select" className={inputCls} value={form.state} onChange={set("state")}>
                      {sortedAbbrs.map((a) => <option key={a} value={a}>{STATES[a][0]}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="w-count">People paid each month</label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range" min="1" max="100" value={Math.min(n, 100)}
                        onChange={(e) => setForm({ ...form, headcount: Number(e.target.value) })}
                        className="w-full accent-emerald-600" aria-label="People paid each month"
                      />
                      <input id="w-count" data-testid="contact-headcount-input" type="number" min="1" max="500"
                        value={form.headcount} onChange={set("headcount")}
                        className="w-24 rounded-xl border border-slate-300 px-3 py-2.5 text-center font-mono-num text-sm font-bold" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="w-current">Current payroll provider</label>
                    <select id="w-current" data-testid="contact-provider-select" className={inputCls} value={form.current_provider} onChange={set("current_provider")}>
                      {["None, first payroll", "Gusto", "ADP", "Paychex", "QuickBooks Payroll", "OnPay", "Other"].map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <h2 className="font-display text-xl font-bold text-slate-900">Pick a plan</h2>
                  <p className="mt-1 text-sm text-slate-500">Prices update live for {n} {n === 1 ? "person" : "people"}. Switch anytime.</p>
                  <div className="mt-5 space-y-3">
                    {Object.values(PLANS).map((pl) => {
                      const total = pl.base + pl.per * n;
                      const selected = form.plan === pl.key;
                      return (
                        <label
                          key={pl.key}
                          data-testid={`wizard-plan-${pl.key}`}
                          className={`flex cursor-pointer items-center justify-between gap-4 rounded-xl border p-4 transition-colors ${
                            selected ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-slate-400"
                          }`}
                        >
                          <span className="flex items-center gap-3">
                            <input type="radio" name="wizard-plan" value={pl.key} checked={selected}
                              onChange={() => setForm({ ...form, plan: pl.key })} className="accent-emerald-600" />
                            <span>
                              <span className="block font-display font-bold text-slate-900">{pl.name}</span>
                              <span className="block text-xs text-slate-500">{pl.fit}</span>
                            </span>
                          </span>
                          <span className="text-right">
                            <span className="block font-mono-num text-lg font-bold text-slate-900">{money(total)}<span className="text-xs font-normal text-slate-500">/mo</span></span>
                            <span className="block text-xs text-slate-400">{money(pl.base)} + {money(pl.per)}/person</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <h2 className="font-display text-xl font-bold text-slate-900">Where should we send your quote?</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls} htmlFor="w-name">Your name</label>
                      <input id="w-name" data-testid="contact-name-input" className={inputCls} required autoComplete="name" value={form.name} onChange={set("name")} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="w-biz">Business name</label>
                      <input id="w-biz" data-testid="contact-business-input" className={inputCls} required autoComplete="organization" value={form.business} onChange={set("business")} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="w-email">Email</label>
                      <input id="w-email" data-testid="contact-email-input" className={inputCls} type="email" required autoComplete="email" value={form.email} onChange={set("email")} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="w-phone">Phone</label>
                      <input id="w-phone" data-testid="contact-phone-input" className={inputCls} type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="w-msg">Anything we should know? (optional)</label>
                    <textarea id="w-msg" data-testid="contact-message-input" rows={3} className={inputCls}
                      placeholder="Other states you pay in, tipped staff, contractors, pay schedule"
                      value={form.message} onChange={set("message")} />
                  </div>
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="w-web">Leave blank</label>
                    <input id="w-web" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex items-center justify-between">
            {step > 0 ? (
              <button key="back" type="button" onClick={back} data-testid="wizard-back-btn"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-700 hover:border-slate-900 transition-colors">
                <ArrowLeft size={15} /> Back
              </button>
            ) : <span />}
            {step < 2 ? (
              <button key="next" type="button" onClick={next} data-testid="wizard-next-btn"
                className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-6 py-3 text-sm font-bold hover:bg-emerald-700 transition-colors">
                Continue <ArrowRight size={15} />
              </button>
            ) : (
              <button key="submit" type="submit" disabled={submitting} data-testid="contact-submit-btn"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-6 py-3 text-sm font-bold hover:bg-emerald-700 transition-colors disabled:opacity-60">
                {submitting && <Loader2 size={15} className="animate-spin" />} Request my quote
              </button>
            )}
          </div>
        </form>

        {/* Live price panel */}
        <aside className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 lg:sticky lg:top-28 shadow-[0_24px_60px_-28px_rgba(15,23,42,0.25)]" data-testid="wizard-price-panel">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-700">Your instant estimate</p>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="font-mono-num text-4xl font-bold text-slate-900" data-testid="wizard-price-total">{money(ours)}</span>
            <span className="text-slate-500">/month</span>
          </div>
          <dl className="mt-5 space-y-2.5 text-sm border-t border-slate-200 pt-4">
            <div className="flex justify-between"><dt className="text-slate-500">{p.name} base fee</dt><dd className="font-mono-num font-bold">{money(p.base)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">{n} {n === 1 ? "person" : "people"} &times; {money(p.per)}</dt><dd className="font-mono-num font-bold">{money(p.per * n)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Multi-state payroll</dt><dd className="font-bold text-emerald-700">Included</dd></div>
            <div className="flex justify-between text-slate-400"><dt>Same team on {p.cname}</dt><dd className="font-mono-num line-through">{money(theirs)}</dd></div>
          </dl>
          <div className="mt-4 flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
            <span className="text-sm font-semibold text-emerald-900">You keep each year</span>
            <strong className="font-mono-num text-lg font-bold text-emerald-700" data-testid="wizard-savings">{money(Math.max(0, (theirs - ours) * 12))}</strong>
          </div>
          <div className="mt-4 rounded-xl border border-slate-200 bg-[#FAFAFA] p-4" data-testid="wizard-state-facts">
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">{STATES[form.state][0]} at a glance</p>
            <dl className="mt-3 space-y-2 text-xs">
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">State income tax on wages</dt>
                <dd className="font-bold text-slate-800" data-testid="wizard-state-fact-income">{NO_WAGE_TAX.has(form.state) ? "No" : "Yes — we file it"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Local payroll taxes</dt>
                <dd className="font-bold text-slate-800">{LOCAL_TAX.has(form.state) ? "In some areas — handled" : "Not typical"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Payroll-funded leave program</dt>
                <dd className="font-bold text-slate-800">{PAID_LEAVE.has(form.state) ? "Yes — we file it" : "No"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">ABSLpay per-state fee</dt>
                <dd className="font-bold text-emerald-700">None</dd>
              </div>
            </dl>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
            Estimate only — your written quote confirms it the same business day. Comparison uses Gusto's published
            prices, checked {SITE.competitorChecked}. Payroll taxes you owe are separate on every provider.
          </p>
        </aside>
      </div>
    </div>
  );
};
